import { produce } from 'immer';
import { createReducer } from 'typesafe-actions';

import {
  getPermissions,
  permissionConfigDefaults,
} from '@portal/common/dist/access-control';
import {
  replaceByIdFromArray,
  replaceByUuidWithIndexes,
} from '@portal/common/dist/utils/store-utils';

import { resetStore } from '../actions';
import type { GlobalAction } from '../actions';
import { nextHydrate } from '../hydrate';
import { reconcileState } from '../utils';

import {
  createUserDeactivationsAction,
  deleteUserDeactivationAction,
  richAccessTokenByIdRequestAction,
  richAccessTokenByUuidRequestAction,
  sendUserDemandContactUsRequestEmailAction,
  setClaims,
  setProfileMeta,
  toggleCreateUser,
  toggleDuplicateOpen,
  userAccessByUuidRequestAction,
  userAccessRequestAction,
  userByUuidRequestAction,
  userDeactivationRequestAction,
  userRequestAction,
} from './actions';
import type { UserAction, UserState } from './types';

export const initialUserState: UserState = {
  accessByUuid: {},
  accessTokenById: {},
  accessTokenByUuid: {},
  byUuid: {},
  contactUsRequest: [],
  createDrawerVisible: false,
  deactivationRows: [],
  duplicateUuid: '',
  featureFlags: [],
  hasMultipleUserExperiences: false,
  haveAllUsersAccessBeenRequested: false,
  haveAllUsersBeenRequested: false,
  haveAllUsersDeactivationBeenRequested: false,
  permissions: permissionConfigDefaults,
  rows: [],
  rowsByUuid: {},
  whitelist: [],
};

export const userReducer = createReducer<UserState, UserAction | GlobalAction>(
  initialUserState
)
  .handleAction(resetStore, () => initialUserState)
  .handleAction(setClaims, (state, { payload }) =>
    produce(state, (draftState) => {
      draftState.claims = payload;
    })
  )
  .handleAction(setProfileMeta, (state, { payload }) =>
    produce(state, (draftState) => {
      draftState.engineUser = payload.engineUser;
      draftState.featureFlags = payload.featureFlags;
      draftState.requestedTier = payload.requestedTier;
      draftState.roles = payload.roles;
      draftState.scopes = payload.scopes;
      draftState.whitelist = payload.whitelist;
      draftState.permissions = getPermissions(payload);
      draftState.profileMeta = payload;
      draftState.hasMultipleUserExperiences =
        payload.hasMultipleUserExperiences;
    })
  )
  .handleAction(userRequestAction.success, (state, { payload }) =>
    produce(state, (draftState) => {
      draftState.haveAllUsersBeenRequested = true;
      draftState.rows = payload;
    })
  )
  .handleAction(userAccessRequestAction.success, (state, { payload }) =>
    produce(state, (draftState) => {
      draftState.haveAllUsersAccessBeenRequested = true;
      payload.forEach((access) => {
        draftState.accessByUuid[access.userUuid] = access;
      });
    })
  )
  .handleAction(userByUuidRequestAction.success, (state, { payload }) =>
    produce(state, (draftState) => {
      const { indexes, rows } = replaceByUuidWithIndexes(state.rows, [payload]);

      draftState.byUuid[payload.uuid] = payload;
      draftState.rows = rows;
      draftState.rowsByUuid[payload.uuid] = indexes[0];
    })
  )
  .handleAction(userAccessByUuidRequestAction.success, (state, { payload }) =>
    produce(state, (draftState) => {
      draftState.accessByUuid[payload.userUuid] = payload;
    })
  )
  .handleAction(toggleCreateUser, (state) =>
    produce(state, (draftState) => {
      draftState.duplicateUuid = '';
      draftState.createDrawerVisible = !state.createDrawerVisible;
    })
  )
  .handleAction(toggleDuplicateOpen, (state, { payload }) =>
    produce(state, (draftState) => {
      draftState.duplicateUuid = payload;
      draftState.createDrawerVisible = !state.createDrawerVisible;
    })
  )
  .handleAction(
    richAccessTokenByUuidRequestAction.success,
    (state, { payload }) =>
      produce(state, (draftState) => {
        draftState.accessTokenByUuid = {
          ...draftState.accessTokenByUuid,
          ...payload,
        };
      })
  )
  .handleAction(
    richAccessTokenByIdRequestAction.success,
    (state, { payload }) =>
      produce(state, (draftState) => {
        draftState.accessTokenById[payload.id] = payload.richAccessToken;
      })
  )
  .handleAction(userDeactivationRequestAction.success, (state, { payload }) =>
    produce(state, (draftState) => {
      draftState.deactivationRows = payload;
      draftState.haveAllUsersDeactivationBeenRequested = true;
    })
  )
  .handleAction(createUserDeactivationsAction.success, (state, { payload }) =>
    produce(state, (draftState) => {
      draftState.deactivationRows = [...state.deactivationRows, ...payload];
    })
  )
  .handleAction(deleteUserDeactivationAction.success, (state, { payload }) =>
    produce(state, (draftState) => {
      draftState.deactivationRows = replaceByIdFromArray(
        state.deactivationRows,
        payload
      );
    })
  )
  .handleAction(
    sendUserDemandContactUsRequestEmailAction.success,
    (state, { payload }) =>
      produce(state, (draftState) => {
        if (state?.contactUsRequest) {
          draftState.contactUsRequest = [...state.contactUsRequest, payload];
        } else {
          draftState.contactUsRequest = [payload];
        }
      })
  )
  .handleAction(nextHydrate, (state, { payload }) => ({
    ...state,
    ...reconcileState(payload.user, initialUserState),
  }));
